import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  AIClass,
  CubeDimensionEnum,
  FeatureEnum,
} from '../../../services/data/impl/cube/cubeSlice';
import { mapFeatureEnumAlias } from '../../../services/data/impl/cube/cubeUtils';
import { isFeatureEnum } from '../../commonUtils';
import useCurrServicesAndMeasures from '../../customerSummary/hooks/useCurrServicesAndMeasures';
import {
  RanchViewFeaturesEnum,
  selectCurrFeature,
  selectCurrRanchViewFeature,
  selectCurrScanID,
  setCurrFeature,
} from '../../select/selectSlice';
import { RanchPageViewEnum, selectRanchPageView } from '../ranchesSlice';

/**
 * `useSetCurrFeature` is a custom hook that updates the current feature
 * based on AI models run on a scan. It sets the current feature to the
 * first model's class name if it's not already in the list of AI model
 * class names.
 */
const useSetCurrFeature = () => {
  const dispatch = useAppDispatch();
  const currFeature = useAppSelector(selectCurrFeature);
  const currScanID = useAppSelector(selectCurrScanID);
  const ranchPageView = useAppSelector(selectRanchPageView);
  const ranchViewFeature = useAppSelector(selectCurrRanchViewFeature);
  const { servicesAndMeasuresWithoutFeatureFilter } =
    useCurrServicesAndMeasures();

  useEffect(() => {
    // if on ranch summary table view, don't change currFeature bc it's used to filter the table by feature and users need to be able to set it to all features without it getting reset
    if (
      ranchPageView === RanchPageViewEnum.ranchSummary &&
      ranchViewFeature === RanchViewFeaturesEnum.Tables
    ) {
      return;
    }

    if (
      servicesAndMeasuresWithoutFeatureFilter.availableServicesRaw?.length > 0
    ) {
      // Only update the current feature if it's valid or an alias of a valid feature
      const validFeatures = validatedClassesRunOnScan(
        servicesAndMeasuresWithoutFeatureFilter.availableServicesRaw
      );

      // Set the current feature to the first valid feature.
      if (validFeatures.length > 0) {
        // If the currently selected feature is in the list of valid features, do nothing.  This way if "shoots" is selected and the user switches to a scan that has "shoots" as a feature, it will stay on "shoots" instead of switching to the first feature in the list.
        if (!validFeatures.includes(currFeature)) {
          dispatch(setCurrFeature(validFeatures[0]));
        }
      }
    }
  }, [
    servicesAndMeasuresWithoutFeatureFilter.availableServicesRaw,
    currFeature,
    currScanID,
    ranchPageView,
    ranchViewFeature,
    dispatch,
  ]);
};

export default useSetCurrFeature;

/**
 * This function filters out invalid features from an array of AI classes and maps valid ones to
 * their corresponding aliases if any exist.
 *
 * @param {AIClass[]} aiClassesRunOnScan - The array of AI classes to validate and map.
 * @returns {FeatureEnum[]} - The validated and mapped array of features.
 */
const validatedClassesRunOnScan = (
  aiClassesRunOnScan: AIClass[]
): FeatureEnum[] => {
  // Filter out invalid features
  const validClassNamesRunOnScan = aiClassesRunOnScan
    .filter((classObject) =>
      isFeatureEnum(classObject[CubeDimensionEnum.className])
    )
    .map((validClass) => validClass[CubeDimensionEnum.className]);

  // Handle aliases
  const features = validClassNamesRunOnScan.map((className) =>
    mapFeatureEnumAlias(className)
  );

  return features;
};
